<script setup lang="ts">

import {Bar} from "vue-chartjs";
import {useI18n} from "vue-i18n";
import {ArcElement, BarElement, CategoryScale, Chart as ChartJS, Legend, LinearScale, Title, Tooltip, ChartOptions} from "chart.js";
import {computed, PropType} from "vue";
import {
  personalityTypeDistributionValues
} from "@/pages/Insights/ReportsDetailPage/reportTypes";

ChartJS.register(Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale, ArcElement);

const {t} = useI18n();

const props = defineProps(
  {
    reportData: {
      type: Object as PropType<personalityTypeDistributionValues>,
      required: true,
    },
    label: {
      type: String as PropType<string>,
      required: true,
    },
  }
);

interface ChartData {
  label?: string;
  labels?: string[];
  datasets: {
    label: string;
    data: number[];
    backgroundColor: string[];
  }[];
}

const createTraitChart = () => computed((): ChartData => {
  if (!props.reportData) return {
    label: props.label,
    datasets: []
  };
  return createChartData(props.reportData, props.label);
});

const barChart = createTraitChart();

/**
 * Creates the data for the pie chart.
 * @param personalityTraitCounts The subject counts to create the chart data from.
 * @param label The label for the chart.
 */
const createChartData = (personalityTraitCounts: personalityTypeDistributionValues, label: string): ChartData => {
  if (!personalityTraitCounts) return {
    labels: [],
    datasets: []
  };

  const labels = {
    "extremely_high": "Extremely High",
    "very_high": "Very High",
    "high": "High",
    "medium": "Medium",
    "low": "Low",
    "very_low": "Very Low"
  };
  const sortedKeys = Object.keys(labels).filter((key) => key in personalityTraitCounts);
  const readableKeys = sortedKeys.map((key) => t(`common.${key}`));

  return {
    labels: readableKeys,
    datasets: [
      {
        label,
        data: sortedKeys.map((key) => personalityTraitCounts[key]),
        backgroundColor: [
          "#003f5c",
          "#444e86",
          "#955196",
          "#dd5182",
          "#ff6e54",
          "#ffa600",
        ]
      },
    ],
  };
};

const chartOptions: ChartOptions<"bar"> = {
  responsive: true,
  plugins: {
    legend: {
      position: "bottom"
    }
  }
};

</script>

<template>
  <Bar :options="chartOptions" :data="barChart"/>
</template>

<style scoped>

</style>
